import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { CustomerItem } from "../../../../mocks/customers";
import PhoneActionMenu from "../../../../components/contact/PhoneActionMenu";

interface Props {
  customer: CustomerItem;
  phoneHighlight?: string;
  nameHighlight?: string;
  salesNoteSummary?: string;
  onSatisEkle?: (customer: CustomerItem) => void;
  onPrint?: (customer: CustomerItem) => void;
}

const DESKTOP_GRID = "190px minmax(190px,1fr) 132px 78px 48px 104px 104px 104px 128px";
const DESKTOP_MIN_WIDTH = 1180;

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 });

function getStatus(c: CustomerItem): { label: string; cls: string } {
  const kalan = c.toplamBorc - c.toplamOdenen;
  if (c.toplamHisseSayisi === 0) return { label: "Kayıtlı", cls: "bg-farm-border/50 text-farm-muted" };
  if (kalan === 0) return { label: "Tam", cls: "bg-farm-green-pale text-farm-green" };
  if (c.toplamOdenen > 0) return { label: "Kısmi", cls: "bg-farm-gold-pale text-farm-gold" };
  return { label: "Bekliyor", cls: "bg-farm-earth-pale text-farm-earth" };
}

function highlightText(text: string, query: string): ReactNode {
  if (!query.trim()) return <span>{text}</span>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, idx)}
      <mark className="bg-farm-gold/30 text-farm-brown rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </span>
  );
}

export { DESKTOP_GRID as CUSTOMER_ROW_GRID, DESKTOP_MIN_WIDTH as CUSTOMER_ROW_MIN_WIDTH };

export default function CustomerRow({
  customer,
  phoneHighlight = "",
  nameHighlight = "",
  salesNoteSummary = "",
  onSatisEkle,
  onPrint,
}: Props) {
  const kalan = customer.toplamBorc - customer.toplamOdenen;
  const status = getStatus(customer);

  return (
    <div className="border-b border-farm-border last:border-0 group">
      <div
        className="hidden xl:grid w-full items-center text-xs font-body hover:bg-farm-gold-pale/15 transition-colors"
        style={{ gridTemplateColumns: DESKTOP_GRID, minWidth: `${DESKTOP_MIN_WIDTH}px` }}
      >
        <div className="px-3 py-2.5 min-w-0">
          <p className="font-semibold text-farm-brown truncate">{highlightText(customer.adSoyad, nameHighlight)}</p>
          {customer.kisaNot && (
            <p className="text-[10px] text-farm-muted truncate mt-0.5 italic">{customer.kisaNot}</p>
          )}
        </div>

        <div className="px-3 py-2.5 min-w-0">
          <p className={`truncate ${salesNoteSummary ? "text-farm-brown" : "text-farm-muted"}`} title={salesNoteSummary || "Satış notu yok"}>
            {salesNoteSummary || "—"}
          </p>
        </div>

        <div className="px-3 py-2.5">
          <PhoneActionMenu
            phone={customer.telefon}
            label={highlightText(customer.telefon, phoneHighlight)}
            buttonClassName="font-body text-xs font-bold text-farm-earth hover:text-farm-brown whitespace-nowrap"
          />
        </div>

        <div className="px-3 py-2.5">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${status.cls}`}>
            {status.label}
          </span>
        </div>

        <div className="px-2 py-2.5 text-center">
          <span className={`font-semibold ${customer.toplamHisseSayisi > 0 ? "text-farm-brown" : "text-farm-muted"}`}>
            {customer.toplamHisseSayisi}
          </span>
        </div>

        <div className="px-2 py-2.5 text-right text-farm-brown whitespace-nowrap">
          {customer.toplamBorc > 0 ? fmt(customer.toplamBorc) : "—"}
        </div>

        <div className="px-2 py-2.5 text-right whitespace-nowrap">
          <span className={customer.toplamOdenen > 0 ? "text-farm-green font-semibold" : "text-farm-muted"}>
            {customer.toplamOdenen > 0 ? fmt(customer.toplamOdenen) : "—"}
          </span>
        </div>

        <div className="px-2 py-2.5 text-right whitespace-nowrap">
          <span className={`font-bold ${kalan > 0 ? "text-farm-earth" : "text-farm-muted"}`}>
            {kalan > 0 ? fmt(kalan) : "—"}
          </span>
        </div>

        <div className="px-2 py-2.5 flex items-center justify-center gap-1.5 whitespace-nowrap">
          <button
            onClick={() => onSatisEkle?.(customer)}
            title="Satış Ekle"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-farm-earth text-white hover:bg-farm-brown transition-colors cursor-pointer"
            type="button"
          >
            <i className="ri-add-line text-sm" />
          </button>
          <button
            type="button"
            onClick={() => onPrint?.(customer)}
            title="Yazdır"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white border border-farm-border text-farm-brown hover:bg-farm-cream transition-colors cursor-pointer"
          >
            <i className="ri-printer-line text-sm" />
          </button>
          <Link
            to={`/panel/musteriler/${customer.id}`}
            title="Detay"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-farm-earth/10 border border-farm-earth/20 text-farm-earth hover:bg-farm-earth hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-eye-line text-sm" />
          </Link>
        </div>
      </div>

      <div className="xl:hidden px-3 py-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="min-w-0">
            <p className="font-body text-sm font-bold text-farm-brown leading-snug truncate">
              {highlightText(customer.adSoyad, nameHighlight)}
            </p>
            {customer.kisaNot && (
              <p className="font-body text-[10px] text-farm-muted italic mt-0.5 leading-tight">{customer.kisaNot}</p>
            )}
            {salesNoteSummary && (
              <p className="font-body text-[10px] text-farm-brown mt-0.5 truncate" title={salesNoteSummary}>
                Satış Notu: {salesNoteSummary}
              </p>
            )}
          </div>
          <span className={`flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${status.cls}`}>
            {status.label}
          </span>
        </div>

        <div className="flex items-center gap-1.5 mb-2">
          <div className="w-4 h-4 flex items-center justify-center text-farm-earth flex-shrink-0">
            <i className="ri-phone-fill text-sm" />
          </div>
          <PhoneActionMenu
            phone={customer.telefon}
            label={highlightText(customer.telefon, phoneHighlight)}
            buttonClassName="font-body text-sm font-bold text-farm-earth hover:text-farm-brown tracking-wide"
          />
          {customer.toplamHisseSayisi > 0 && (
            <span className="ml-auto font-body text-[10px] text-farm-muted whitespace-nowrap">
              {customer.toplamHisseSayisi} hisse
            </span>
          )}
        </div>

        {customer.toplamBorc > 0 && (
          <div className="grid grid-cols-3 gap-2 text-[10px] font-body bg-farm-cream border border-farm-border rounded px-2 py-1.5 mb-2">
            <div>
              <p className="text-farm-muted mb-0.5">Toplam</p>
              <p className="text-farm-brown font-semibold">{fmt(customer.toplamBorc)}</p>
            </div>
            <div>
              <p className="text-farm-muted mb-0.5">Ödenen</p>
              <p className={`font-semibold ${customer.toplamOdenen > 0 ? "text-farm-green" : "text-farm-muted"}`}>
                {customer.toplamOdenen > 0 ? fmt(customer.toplamOdenen) : "—"}
              </p>
            </div>
            <div>
              <p className="text-farm-muted mb-0.5">Kalan</p>
              <p className={`font-semibold ${kalan > 0 ? "text-farm-earth" : "text-farm-muted"}`}>
                {kalan > 0 ? fmt(kalan) : "—"}
              </p>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => onSatisEkle?.(customer)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-earth text-white font-body text-[10px] font-semibold hover:bg-farm-brown transition-colors cursor-pointer whitespace-nowrap"
            type="button"
          >
            <i className="ri-add-line text-xs" />
            Satış Ekle
          </button>
          <button
            onClick={() => onPrint?.(customer)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-farm-border text-farm-brown font-body text-[10px] font-semibold hover:bg-farm-cream transition-colors cursor-pointer whitespace-nowrap"
            type="button"
          >
            <i className="ri-printer-line text-xs" />
            Yazdır
          </button>
          <Link
            to={`/panel/musteriler/${customer.id}`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-earth/10 border border-farm-earth/20 text-farm-earth font-body text-[10px] font-semibold hover:bg-farm-earth hover:text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-eye-line text-xs" />
            Detay
          </Link>
          <PhoneActionMenu
            phone={customer.telefon}
            label={<><i className="ri-phone-line text-xs" /> Ara</>}
            buttonClassName="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-farm-green/10 border border-farm-green/20 text-farm-green font-body text-[10px] font-semibold hover:bg-farm-green hover:text-white whitespace-nowrap"
          />
        </div>
      </div>
    </div>
  );
}
